// AnnouncementSelect, not Announcement: the admin route returns the bare row
// without the updatedByUser relation.
import type { AnnouncementSelect, PublicAnnouncement } from 'types';
import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useClient } from 'hooks/useClient';
import { config } from 'config';

const POLL_INTERVAL = 60_000;

export type AnnouncementFormValues = {
  active: boolean;
  severity: PublicAnnouncement['severity'];
  title: string;
  message: string;
  ctaLabel: string;
  ctaUrl: string;
};

// Keyed on updatedAt, so editing a message re-shows it to everyone who had
// already dismissed the previous one. Session scoped: a dismissal should not
// outlive the tab.
const dismissKey = (announcement: PublicAnnouncement) => `PERTENTO-ANNOUNCEMENT-${announcement.updatedAt ?? 0}`;

const isDismissed = (announcement: PublicAnnouncement): boolean => {
  if (typeof window === 'undefined') return false;
  return !!window.sessionStorage.getItem(dismissKey(announcement));
};

/**
 * Reads the public route, so this works on the signin screen where there is no
 * session yet.
 *
 * Deliberately a bare fetch rather than useClient: useClient routes every
 * response over 400 into the global API error dialog, and a background poll
 * that opens a dialog when the endpoint hiccups is worse than one that shows
 * nothing. A missing announcement is never an error worth interrupting anyone.
 */
export const useAnnouncement = () => {
  const [, forceUpdate] = useState(0);

  const { data } = useQuery<PublicAnnouncement | null>({
    queryKey: ['ANNOUNCEMENT'],
    queryFn: async () => {
      try {
        const res = await fetch(`${config.API_URL}/announcements/active`);
        if (!res.ok) return null;
        return (await res.json()) as PublicAnnouncement | null;
      } catch {
        return null;
      }
    },
    refetchInterval: POLL_INTERVAL,
    refetchOnWindowFocus: true,
    staleTime: POLL_INTERVAL,
    retry: false,
  });

  const announcement = data ?? null;

  const dismiss = () => {
    if (!announcement) return;
    window.sessionStorage.setItem(dismissKey(announcement), 'true');
    forceUpdate((n) => n + 1);
  };

  return {
    announcement: announcement && !isDismissed(announcement) ? announcement : null,
    dismiss,
  };
};

/**
 * The Super Admin editing surface. Unlike the public read this returns the row
 * whatever its state, because editing an unpublished draft is the point.
 */
export const useAnnouncementAdmin = () => {
  const client = useClient();
  const queryClient = useQueryClient();

  const { data, isLoading } = useQuery<AnnouncementSelect>({
    queryKey: ['ANNOUNCEMENT_ADMIN'],
    queryFn: () => client.get<AnnouncementSelect>('/announcements'),
  });

  const save = async (values: AnnouncementFormValues) => {
    const saved = await client.put<AnnouncementSelect>('/announcements', values);
    queryClient.setQueryData(['ANNOUNCEMENT_ADMIN'], saved);
    // So the author sees their own change in the bar without a reload.
    queryClient.invalidateQueries({ queryKey: ['ANNOUNCEMENT'] });
    return saved;
  };

  return { announcement: data, isLoading, save };
};
